import React from 'react'
import { styled } from 'styled-components'
import type { StudentWithPoints } from '@mission.io/mission-toolkit'
import type { AbstractComponent } from 'react'

type Props = {
	student: StudentWithPoints,
	className?: ?string,
	draggable?: boolean,
	isDragging?: boolean,
}

/**
 * Displays the given `student`'s name and whether they are connected to the mission.
 * Employs a grid layout to ensure that the drag icon and student name are correctly centered and positioned without using position absolute
 */
export const Student: AbstractComponent<Props> = React.forwardRef(
	function Student({ student, className, draggable }: Props, ref): React$Node {
		const DraggableIcon = ({ draggable, className }) =>
			draggable ? <div className={className}>⠿</div> : <div /> // Empty div to preserve grid structure
		return (
			<PlayerStudentContainer
				ref={ref}
				$connected={student.connected}
				className={`${
					className ?? ''
				} grid grid-cols-[auto_1fr_auto] items-center`}
				$draggable={draggable}>
				<DraggableIcon draggable={draggable} />

				<div className="text-center">
					{student.firstName} {student.lastName}
				</div>

				<DraggableIcon
					draggable={draggable}
					className="text-transparent select-none"
					aria-hidden="true"
				/>
			</PlayerStudentContainer>
		)
	}
)

const PlayerStudentContainer = styled.div`
	border-radius: 4px;
	opacity: 1;
	text-align: center;

	${({ $connected, theme, $draggable }) => `
		background-color: ${$connected ? theme.highlight : theme.base3};
		color: ${$connected ? theme.base3 : theme.highlight};	
        box-shadow: 0px 3px 6px ${theme.boxShadow};
		&:hover {
			opacity: ${$draggable ? 0.85 : 1};
		}	
	`}
`
